// calendarTemporalFields.js — shared temporal field helpers for calendar create/prefill

import { addDays, addHours, startOfDay } from 'date-fns';

import { timestampFromDate } from 'utilities/DateTime';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

const PREFERRED_FIELD_PATTERNS = [/^due/i, /^start/i, /date/i];

/**
 * Returns display schema fields that are calendar-capable (temporal).
 *
 * @param {object} typeDef - Node type definition.
 * @param {object} [options]
 * @param {string} [options.preferredFieldKey] - Optional field name/key to narrow to.
 * @returns {object[]} Temporal field defs.
 */
export const getCalendarDateFields = (typeDef, options = {}) => {
  if (!typeDef) return [];

  const preferredFieldKey = String(
    options?.preferredFieldKey || options?.calendarField || '',
  ).trim();

  const includeHidden = options.includeHidden === true;

  const fields = resolveDisplaySchemaFields(typeDef, { includeHidden });
  const rows = (Array.isArray(fields) ? fields : []).filter((field) => {
    const fieldType = String(field?.type || '').toLowerCase();
    const fieldKey = field?.name || field?.key;
    return fieldKey && isTemporalFieldType(fieldType);
  });

  if (!preferredFieldKey) return rows;

  return rows.filter((field) => (
    field?.name === preferredFieldKey || field?.key === preferredFieldKey
  ));
};

/**
 * Whether a type has at least one display temporal field suitable for calendar placement.
 *
 * @param {object} typeDef - Node type definition.
 * @returns {boolean}
 */
export const typeHasCalendarFields = (typeDef) => (
  getCalendarDateFields(typeDef).length > 0
);

/**
 * Resolves the temporal field that can be seeded without asking the user.
 * A configured calendar field wins; otherwise only an unambiguous single
 * temporal field is selected.
 *
 * @param {object} typeDef - Node type definition.
 * @param {object} [options]
 * @param {string} [options.preferredFieldKey]
 * @returns {{ fields: object[], targetField: object|null }}
 */
export const resolveCalendarCreateField = (typeDef, options = {}) => {
  const fields = getCalendarDateFields(typeDef);
  const preferredFieldKey = String(
    options?.preferredFieldKey || options?.calendarField || '',
  ).trim();
  const preferredField = preferredFieldKey
    ? fields.find((field) => (
      field?.name === preferredFieldKey || field?.key === preferredFieldKey
    )) || null
    : null;

  return {
    fields,
    targetField: preferredField || (fields.length === 1 ? fields[0] : null),
  };
};

/**
 * Picks the preferred calendar field from a list (due / start / date heuristics).
 *
 * @param {object[]} fields - Temporal field defs.
 * @returns {object|null}
 */
export const pickPreferredCalendarField = (fields = []) => {
  const rows = Array.isArray(fields) ? fields : [];

  for (const pattern of PREFERRED_FIELD_PATTERNS) {
    const match = rows.find((field) => (
      pattern.test(String(field?.name || field?.key || ''))
    ));
    if (match) return match;
  }

  return rows[0] || null;
};

/**
 * Picks the preferred field key from QuickAdd-style dateFieldOptions.
 *
 * @param {object[]} options - { fieldKey, ... } rows.
 * @returns {string}
 */
export const getDefaultTemporalFieldKey = (options) => {
  const list = Array.isArray(options) ? options : [];
  if (!list.length) return '';

  for (const pattern of PREFERRED_FIELD_PATTERNS) {
    const match = list.find((item) => pattern.test(item?.fieldKey || ''));
    if (match?.fieldKey) return match.fieldKey;
  }

  return list[0]?.fieldKey || '';
};

/**
 * Builds a field value shape for a temporal field type from a clicked calendar date.
 *
 * @param {object} params
 * @param {string} params.fieldType - Temporal field type.
 * @param {Date|string|number} params.date - Clicked date/time.
 * @param {boolean} [params.includeTime=false] - Whether the click included a time.
 * @returns {object|null}
 */
export const buildTemporalValueForField = ({ fieldType, date, includeTime = false }) => {
  const safeDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(safeDate.getTime())) return null;

  const normalizedType = String(fieldType || '').toLowerCase();

  if (normalizedType === 'date') {
    const day = startOfDay(safeDate);
    return {
      startDate: timestampFromDate(day),
      startPrecision: 'date',
      endDate: null,
      endPrecision: null,
      isRange: false,
    };
  }

  if (normalizedType === 'annualdate') {
    return {
      month: safeDate.getMonth() + 1,
      day: safeDate.getDate(),
    };
  }

  if (normalizedType === 'datetime') {
    const valueDate = includeTime ? safeDate : startOfDay(safeDate);
    return {
      startDate: timestampFromDate(valueDate),
      startPrecision: includeTime ? 'time' : 'date',
      endDate: null,
      endPrecision: null,
      isRange: false,
    };
  }

  if (normalizedType === 'daterange') {
    const day = startOfDay(safeDate);
    const end = addDays(day, 1);
    return {
      startDate: timestampFromDate(day),
      startPrecision: 'date',
      endDate: timestampFromDate(end),
      endPrecision: 'date',
      isRange: true,
    };
  }

  if (normalizedType === 'datetimerange') {
    const start = includeTime ? safeDate : startOfDay(safeDate);
    const end = includeTime ? addHours(start, 1) : addDays(start, 1);
    return {
      startDate: timestampFromDate(start),
      startPrecision: includeTime ? 'time' : 'date',
      endDate: timestampFromDate(end),
      endPrecision: includeTime ? 'time' : 'date',
      isRange: true,
    };
  }

  return null;
};

/**
 * Builds initialFields for creating a node on a calendar date.
 *
 * @param {object} typeDef - Node type definition.
 * @param {Date|string|number} date - Clicked date/time.
 * @param {object} [options]
 * @param {boolean} [options.includeTime=false]
 * @param {string} [options.preferredFieldKey]
 * @returns {object|null} Map of fieldKey → value, or null if no suitable field.
 */
export const buildInitialFieldsForCalendarDate = (typeDef, date, options = {}) => {
  const dateFields = getCalendarDateFields(typeDef, options);
  const preferred = pickPreferredCalendarField(dateFields);
  if (!preferred) return null;

  const fieldKey = preferred?.name || preferred?.key;
  const fieldType = String(preferred?.type || '').toLowerCase();
  if (!fieldKey || !fieldType) return null;

  const value = buildTemporalValueForField({
    fieldType,
    date,
    includeTime: Boolean(options?.includeTime),
  });
  if (!value) return null;

  return { [fieldKey]: value };
};
